import React, { useState, useEffect, useMemo } from 'react'
import Debug from 'debug'
import { cleanPayload } from './utils'
import AmazonFieldSummary from './AmazonFieldSummary'
import { useStores } from '../../stores/RootStore'
import { Loadable, ErrorAlert } from '../../components'
import { SaveButton } from '../../components/SaveButton'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { ResourceList } from '../../components/SearchableLists'
import { GET_CHANNEL_FIELDS, UPDATE_CHANNEL_FIELDS } from './commonQueries'

import './ChannelManagement.css'

const debug = Debug('sd:AmazonChannelManagement')

export const AmazonChannelManagement = ({ slug, instanceNumber }) => {
  // Get organizationId from Auth
  const { stores: { auth } } = useStores()
  const { user } = auth || {}
  const { organizationId } = user || {}

  // Query stuff with slug, instanceNumber and organizationId
  const { loading, error, data } = useQuery(GET_CHANNEL_FIELDS, {
    variables: {
      slug,
      instanceNumber,
      organizationId
    }
  })

  // orgMappableFields -> options to select
  const { OrganizationDetails } = data || { OrganizationDetails: {} }
  const { mappableProductFields: orgMappableFields } = OrganizationDetails || []

  // channelMappableFields -> rows
  const { Channel } = data || { Channel: {} }
  const { mappableProductFields: channelMappableFields } = Channel || []

  // channelInstanceFieldMappings -> info about selected option per row
  const { ChannelInstance } = data || { ChannelInstance: {} }
  const { fieldMappings: channelInstanceFieldMappings } = ChannelInstance || []

  const [rows, setRows] = useState([])
  const [newChannelInstance, setNewChannelInstance] = useState()
  const [fieldsToRemoveValue, setFieldsToRemoveValue] = useState([])

  // Amazon Field Summary persistent state
  const [expandedStates, setExpandedStates] = useState({}) // fieldName -> boolean
  const [listItemsStates, setListItemsStates] = useState({}) // fieldName -> array

  // Mutation stuff
  const [mutationError, setMutationError] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [updateFields, { loading: mLoading, data: mData }] = useMutation(
    UPDATE_CHANNEL_FIELDS,
    {
      onCompleted: () => {
        setIsDirty(false)
      },
      onError: (err) => {
        debug('onError, e:', err)
        setMutationError(err || 'There was an error updating field mappings. Try again please.')
      }
    }
  )

  useEffect(() => {
    const rows = channelMappableFields
    const config = channelInstanceFieldMappings

    const childMappings = {}
    const parentMappings = {}

    // Separate parent and child mappings
    if (config && Array.isArray(config)) {
      config.forEach(mapping => {
        if (!mapping) return // Skip null/undefined mappings

        const { organizationFieldName, channelFieldName } = mapping

        if (organizationFieldName && organizationFieldName.includes(' - ')) {
          const [parentFieldName, productType] = organizationFieldName.split(' - ')

          if (!childMappings[parentFieldName]) {
            childMappings[parentFieldName] = []
          }

          const selectedOption = (orgMappableFields || []).find(field => field.name === channelFieldName)

          childMappings[parentFieldName].push({
            id: `${parentFieldName} - ${productType}`,
            productType: { value: productType, label: productType },
            selectedOption: selectedOption
              ? {
                  value: selectedOption.name,
                  label: <><strong style={{ fontWeight: 600, letterSpacing: '.3px' }}>{selectedOption.name}</strong><br />{selectedOption.description}</>
                }
              : null,
            inputValue: { value: channelFieldName } // For input fields
          })
        } else if (organizationFieldName) {
          parentMappings[organizationFieldName] = mapping
        }
      })
    }

    setListItemsStates(childMappings)

    // Auto-expand fields that have child mappings
    const newExpandedStates = {}
    Object.keys(childMappings).forEach(parentFieldName => {
      if (childMappings[parentFieldName].length > 0) {
        newExpandedStates[parentFieldName] = false
      }
    })
    setExpandedStates(prev => ({ ...prev, ...newExpandedStates }))

    // Creates a copy of channelMappableFields but with formatted data
    // Only include parent field mappings (not child mappings)
    const fullRows = (rows || []).map((i, idx) => ({
      ...i,
      value: parentMappings[i.name], // Use parent mappings only
      key: (i.channelFieldName || i.name) + idx,
      generalIndex: idx
    }))
    setRows(fullRows)
  }, [channelMappableFields, channelInstanceFieldMappings, orgMappableFields])

  // State management functions for Amazon fields
  const handleExpandedStateChange = (fieldName, isExpanded) => {
    setExpandedStates(prev => ({
      ...prev,
      [fieldName]: isExpanded
    }))
  }

  const handleListItemsChange = (fieldName, listItems) => {
    setIsDirty(true)
    setMutationError(false)

    setListItemsStates(prev => ({
      ...prev,
      [fieldName]: listItems
    }))
  }

  const handleChangedValue = (event, field) => {
    setIsDirty(true)
    setMutationError(false)

    if (field.parentField) {
      if (!event?.value) {
        setFieldsToRemoveValue(prev => [...prev, field.name])
      } else {
        setFieldsToRemoveValue(prev => prev?.filter(i => i !== field.name))
      }

      const newConfig = {
        channelFieldName: (event && event.value) || null,
        organizationFieldName: field.name
      }

      // Add to newChannelInstance without updating rows
      setNewChannelInstance(prev => {
        const existing = prev || []
        const filtered = existing.filter(item => item.organizationFieldName !== field.name)
        const result = [...filtered, newConfig]
        return result
      })
    } else {
      if (!event?.value) {
        setFieldsToRemoveValue(prev => [...prev, field.name])
      } else {
        setFieldsToRemoveValue(prev => prev?.filter(i => i !== field.name))
      }

      const newConfig = {
        channelFieldName: (event && event.value) || null,
        organizationFieldName: field.name
      }

      // Updates the copy of rows with the new config
      rows[field.generalIndex].value = newConfig

      // Get rows with selected option only and format it to make the mutation
      const valuesToSaveOnly = rows
        .filter(i => (i.value && i.value.channelFieldName) ? i : null)
        .map(i => i.value)

      setNewChannelInstance(valuesToSaveOnly)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    // Collect child field mappings from listItemsStates
    const childFieldMappings = []
    if (listItemsStates && typeof listItemsStates === 'object') {
      Object.keys(listItemsStates).forEach(parentFieldName => {
        const listItems = listItemsStates[parentFieldName] || []
        if (Array.isArray(listItems)) {
          listItems.forEach(item => {
            if (item && (item.selectedOption?.value || item.inputValue?.value)) {
              const productTypeValue = item.productType?.value || item.productType?.label || item.productType
              const childFieldName = `${parentFieldName} - ${productTypeValue}`
              const channelFieldName = item.selectedOption?.value || item.inputValue?.value

              childFieldMappings.push({
                organizationFieldName: childFieldName,
                channelFieldName
              })
            }
          })
        }
      })
    }

    let mergedOldAndNewConfigs
    if (newChannelInstance) {
      mergedOldAndNewConfigs = [...(channelInstanceFieldMappings || []), ...newChannelInstance, ...childFieldMappings]
    } else {
      mergedOldAndNewConfigs = [...(channelInstanceFieldMappings || []), ...childFieldMappings]
    }

    // Removes duplicated keys and keep the latest updated
    const filtered = cleanPayload(mergedOldAndNewConfigs, fieldsToRemoveValue)

    updateFields({
      variables: {
        slug,
        instanceNumber,
        fieldMappings: filtered
      }
    })
  }

  const memoizedList = useMemo(() => {
    return (
      <ResourceList
        className='channel-management'
        items={rows}
        searchableStrings={a => [a.organizationFieldName, a.label]}
        itemComponent={
          ({ item: field, searchTerms }) =>
            <li className='list-group-item-flush d-flex flex-sm-row flex-column list-group-item-action list-group-item'>
              <AmazonFieldSummary
                isListItem
                slug={slug}
                instanceNumber={instanceNumber}
                field={field}
                searchTerms={searchTerms}
                parentCallback={handleChangedValue}
                orgMappableFields={orgMappableFields}
                isExpanded={expandedStates[field.name] || false}
                onExpandedChange={(isExpanded) => handleExpandedStateChange(field.name, isExpanded)}
                listItems={listItemsStates[field.name] || []}
                onListItemsChange={(listItems) => handleListItemsChange(field.name, listItems)}
              />
            </li>
        }
      />
    )
  }, [rows, expandedStates, listItemsStates, orgMappableFields])

  return (
    <>
      <Loadable
        inline
        error={error}
        loading={loading}
        style={{ textAlign: 'center' }}
        content={
          <form className='form p-1' onSubmit={handleSubmit}>
            <p
              className='text-muted'
            >
              You can override the default field mapping for each product type.
            </p>
            {memoizedList}

            {mutationError && <ErrorAlert error={mutationError} />}

            <div className='d-flex justify-content-end'>
              <SaveButton
                type='submit'
                color='primary'
                loading={mLoading}
                success={mData ? 'true' : null}
                dirtysincelastsubmit={isDirty ? 'true' : null}
                disabled={!isDirty || ((newChannelInstance?.length || 0) < 1 && (!listItemsStates || Object.keys(listItemsStates).every(key => (listItemsStates[key]?.length || 0) === 0)))}
              />
            </div>
          </form>
        }
      />
    </>
  )
}

debug('loaded')
